"use client";

import { createElement, useEffect, useRef, type ReactNode } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}
type Props = {
  children: string;
  className?: string;
  delay?: number;
  trigger?: "load" | "scroll";
  as?: "h1" | "h2" | "h3" | "p" | "div" | "span";
  renderWord?: (word: string, index: number) => ReactNode;
};
export function WordReveal({
  children,
  className = "",
  delay = 0,
  trigger = "load",
  as = "h1",
  renderWord
}: Props) {
  const rootRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const spans = root.querySelectorAll<HTMLSpanElement>(".l2-reveal-word > span");
    if (!spans.length) return;
    const tween = gsap.to(spans, {
      yPercent: 0,
      duration: 1.1,
      stagger: 0.045,
      ease: "expo.out",
      delay,
      paused: trigger === "scroll"
    });
    let st: ScrollTrigger | undefined;
    if (trigger === "scroll") {
      st = ScrollTrigger.create({
        trigger: root,
        start: "top 80%",
        once: true,
        onEnter: () => tween.play()
      });
    }
    return () => {
      tween.kill();
      st?.kill();
    };
  }, [delay, trigger]);
  const words = children.split(" ");

  // translate="no" : Google Translate (Chrome Android surtout) regroupe les
  // nœuds texte de la phrase dans des <font> pour la traduire — ce qui casse
  // la réconciliation React sur ce texte découpé en spans (NotFoundError:
  // insertBefore, vu en prod sur /analyse-cv). On exclut uniquement ce texte
  // animé de la traduction ; le reste de la page reste traduisible.
  return createElement(as, {
    className,
    ref: rootRef,
    translate: "no"
  }, words.map((w, i) => <span key={i} className="l2-reveal-word" style={{
    marginRight: "0.25em"
  }}>
        <span>{renderWord ? renderWord(w, i) : w}</span>
      </span>));
}